import { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Send, MessageCircle, ArrowLeft, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, updateBalance, API } = useContext(AppContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const previousMessagesCount = useRef(0);

  useEffect(() => {
    fetchConversations();
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      if (selectedConversation) {
        fetchMessages(selectedConversation.other_user_id, true);
      } else {
        fetchConversations(true);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // If userId is provided in URL, open that conversation
    if (userId && !loading) {
      const conv = conversations.find(c => c.other_user_id === userId);
      if (conv) {
        handleSelectConversation(conv);
      } else {
        // Create a new conversation with this user
        fetchUserInfo(userId);
      }
    }
  }, [userId, conversations, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (!silent) toast.error('Failed to load conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchUserInfo = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/user/${otherUserId}`);
      setSelectedConversation({
        other_user_id: otherUserId,
        other_username: response.data.username,
        last_message: '',
        last_message_time: new Date(),
        unread_count: 0
      });
      setMessages([]);
    } catch (error) {
      console.error('Error fetching user info:', error);
      toast.error('User not found');
      navigate('/messages');
    }
  };

  const fetchMessages = async (otherUserId, silent = false) => {
    try {
      const response = await axios.get(`${API}/messages/${otherUserId}`);
      const newMessages = response.data;
      
      // Check for new incoming messages during silent refresh (auto-polling)
      if (silent && messages.length > 0 && newMessages.length > messages.length) {
        const latestMessage = newMessages[newMessages.length - 1];
        
        // Only show notification if the latest message is from the other user (incoming)
        if (latestMessage.sender_id !== user?.id) {
          toast(
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">New message from {latestMessage.sender_username}</p>
                <p className="text-sm text-gray-600 mt-1">{latestMessage.content}</p>
              </div>
            </div>,
            {
              duration: 4000,
              className: 'bg-green-50 border-green-200',
            }
          );
        }
      }
      
      setMessages(newMessages);
      previousMessagesCount.current = newMessages.length;
      
      // Update balance to refresh unread count
      if (!silent) updateBalance();
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (!silent) toast.error('Failed to load messages');
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.other_user_id);
    navigate(`/messages/${conversation.other_user_id}`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        receiver_id: selectedConversation.other_user_id,
        content: newMessage.trim()
      });
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      
      // Refresh conversations to update last message
      fetchConversations(true);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setMessages([]);
    navigate('/messages');
    fetchConversations(true);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="text-center py-8">Loading messages...</div>;
  }

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Messages</h1>
        <p className="text-gray-600">Chat with other users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className={`lg:col-span-1 ${selectedConversation ? 'hidden lg:block' : ''}`}>
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Conversations</CardTitle>
            </CardHeader>
            <CardContent>
              {conversations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No messages yet</p>
                  <p className="text-gray-400 text-xs mt-1">Start a conversation from user profiles</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <div
                      key={conv.other_user_id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedConversation?.other_user_id === conv.other_user_id
                          ? 'bg-purple-100 border-2 border-purple-600'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid={`conversation-${conv.other_user_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800 truncate">
                              {conv.other_username}
                            </p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-red-600 text-white text-xs px-2">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-1">
                            {conv.last_message}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {formatTime(conv.last_message_time)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Message Thread */}
        <div className={`lg:col-span-2 ${!selectedConversation ? 'hidden lg:block' : ''}`}>
          <Card className="bg-white shadow-lg h-[600px] flex flex-col">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleBackToList}
                      className="lg:hidden"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <CardTitle className="text-lg">
                      {selectedConversation.other_username}
                    </CardTitle>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No messages yet</p>
                      <p className="text-gray-400 text-sm mt-1">Send a message to start the conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isSent = msg.sender_id === user?.id;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg px-4 py-2 ${
                              isSent
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <p className="text-sm break-words">{msg.content}</p>
                            <p
                              className={`text-xs mt-1 ${
                                isSent ? 'text-purple-200' : 'text-gray-500'
                              }`}
                            >
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Message Input */}
                <div className="border-t p-4">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sending}
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      data-testid="send-message-button"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Select a conversation to start messaging</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Messages;
